import { SchemaTypes } from '@allesauto/graphql';
import { useScreenBreakpoint } from '@allesauto/utils/hooks';
import clsx from 'clsx';
import React, { FC, useEffect, useRef, useState } from 'react';
import useDraggableScroll from 'use-draggable-scroll';

import './CarouselThumbnails.css';

import { GalleryImage } from '../gallery';
import VehicleImage, { VehicleImageProps } from '../vehicleImage';

interface CarouselThumbnailsProps {
  carouselItems?:
    | SchemaTypes.VehicleImage[]
    | SchemaTypes.ProductImage[]
    | GalleryImage[];
  carouselState?: {
    slidesToShow: number;
    currentSlide: number;
    totalItems: number;
  };
  goToSlide?: (index: number) => void;
  imageAspectRatio?: VehicleImageProps['aspectRatio'];
  classNames?: string;
  handleSlideChange?: (value: number) => void;
}

const CarouselThumbnails: FC<CarouselThumbnailsProps> = ({
  carouselItems,
  classNames,
  goToSlide,
  carouselState = {
    currentSlide: 0,
    slidesToShow: 0,
    totalItems: 0,
  },
  handleSlideChange,
  imageAspectRatio,
}) => {
  const { currentSlide, totalItems, slidesToShow } = carouselState;
  const [hasOverflow, setHasOverflow] = useState<boolean>();
  const listRef = useRef<HTMLUListElement>(null);
  const { onMouseDown } = useDraggableScroll(listRef);
  const breakpoint = useScreenBreakpoint();
  const hideDots = totalItems === slidesToShow && 'hidden';

  const className = {
    dotListClass: clsx(
      '!mb-10px !pb-5px !px-0 col-end-25 col-start-1 react-multi-carousel-dot-list w-full md:!col-start-2 md:col-end-24',
      hideDots,
      hasOverflow &&
        '!justify-start  md:!col-start-1 md:!col-end-auto lg:!pl-60px md:!pl-30px overflow-x-scroll react-multi-carousel-dot-list-overflow',
      classNames
    ),
    thumbnailImageClass: 'pointer-events-none',
  };

  useEffect(() => {
    if (listRef && listRef.current) {
      setHasOverflow(listRef.current.scrollWidth > listRef.current.clientWidth);
    }
  }, [breakpoint, listRef]);

  useEffect(() => {
    if (handleSlideChange) {
      handleSlideChange(currentSlide);
    }
  }, [currentSlide, handleSlideChange]);

  const thumbnailComponent =
    carouselItems &&
    carouselItems.length > 1 &&
    carouselItems.map((item, index: number) => (
      <li
        key={index}
        onClick={() => goToSlide?.(index)}
        onKeyPress={() => goToSlide?.(index)}
      >
        <button
          type="button"
          className={clsx(
            'cursor-pointer overflow-hidden p-[8px] relative w-[80px] md:w-[120px]',
            index === currentSlide &&
              "after:content-[''] after:bottom-0 after:left-0 after:absolute after:w-full after:h-5px after:bg-green60 monochrome:after:bg-grey90 wl-bg-highlight-after bg-grey10"
          )}
        >
          <VehicleImage
            aspectRatio={imageAspectRatio}
            filename={
              'externalUrlLarge' in item
                ? item.externalUrlLarge
                : 'url' in item
                ? item.url ?? undefined
                : undefined
            }
            transformation="contain"
          />
        </button>
      </li>
    ));

  return (
    <ul
      onMouseDown={hasOverflow ? onMouseDown : undefined}
      ref={listRef}
      className={className.dotListClass}
    >
      {thumbnailComponent}
    </ul>
  );
};

export default CarouselThumbnails;
